import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Copy, Mail, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { TicketCategoryPicker } from "./TicketCategoryPicker";

const REASON_OTHER = "Other (please specify)";

const initialForm = () => ({
  member_email: "",
  member_user_id: "",
  amount: "",
  booking_reason: "",
  booking_reason_other: "",
  expires_in_minutes: 30,
  notes: "",
  custom_row_id: "",
});

export const AdminManualBookingDialog = ({
  isOpen,
  onClose,
  event,
  onCreated,
}) => {
  const [form, setForm] = useState(initialForm);
  const [reasons, setReasons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { checkout_url, email_sent, booking_id }

  // Member search
  const [memberQuery, setMemberQuery] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm());
    setResult(null);
    setMemberQuery("");
    setMemberResults([]);
    setSelectedMember(null);
    axios
      .get("admin/manual-booking-reasons")
      .then((r) => setReasons(r.data?.reasons || []))
      .catch(() => setReasons([
        "Transport only",
        "Tickets with reduced view",
        "Visiting Family Members",
        "Present Ticket",
        "Additional minor(s)",
        "Waiting list",
        REASON_OTHER,
      ]));
  }, [isOpen]);

  // Debounced admin member search (by member_number / email / first / last name)
  useEffect(() => {
    const q = memberQuery.trim();
    if (selectedMember || q.length < 2) {
      setMemberResults([]);
      return;
    }
    let cancelled = false;
    setMemberSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get("admin/users/search", { params: { q } });
        if (!cancelled) setMemberResults(res.data?.results || []);
      } catch {
        if (!cancelled) setMemberResults([]);
      } finally {
        if (!cancelled) setMemberSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [memberQuery, selectedMember]);

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pickMember = (m) => {
    setSelectedMember(m);
    setMemberQuery("");
    setMemberResults([]);
    setForm((p) => ({ ...p, member_email: m.email, member_user_id: m.id }));
  };

  const clearMember = () => {
    setSelectedMember(null);
    setForm((p) => ({ ...p, member_email: "", member_user_id: "" }));
  };

  const isOther = form.booking_reason === REASON_OTHER;
  const canSubmit =
    !!selectedMember &&
    parseFloat(form.amount) > 0 &&
    !!form.booking_reason &&
    (!isOther || form.booking_reason_other.trim().length > 0) &&
    !isSubmitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || !event?.id) return;
    setIsSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(
        `admin/events/${event.id}/manual-bookings`,
        {
          member_email: form.member_email.trim().toLowerCase(),
          amount: parseFloat(form.amount),
          booking_reason: form.booking_reason,
          booking_reason_other: isOther ? form.booking_reason_other.trim() : "",
          expires_in_minutes: parseInt(form.expires_in_minutes, 10) || 30,
          notes: form.notes,
          custom_row_id: form.custom_row_id || null,
        }
      );
      setResult(res.data);
      toast.success(
        res.data?.email_sent
          ? "Booking created — payment link emailed"
          : "Booking created — email failed, copy the link manually"
      );
      onCreated?.();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to create manual booking"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-xl"
        data-testid="admin-manual-booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-700" />
            Create manual booking
            {event?.title && (
              <Badge
                variant="outline"
                className="ml-2 bg-indigo-50 border-indigo-200 text-indigo-700 truncate max-w-[220px]"
              >
                {event.title}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="manual-booking-success">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-emerald-900">
                  Manual booking created
                </p>
                <p className="text-emerald-800 mt-1">
                  {result.email_sent
                    ? "The payment link has been emailed to the member."
                    : "Email send failed — share the link manually."}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">
                Stripe payment link
              </Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={result.checkout_url || ""}
                  onFocus={(e) => e.target.select()}
                  className="font-mono text-xs"
                  data-testid="manual-booking-link-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() => handleCopy(result.checkout_url)}
                  data-testid="copy-manual-booking-link-btn"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
              {result.expires_at && (
                <p className="text-xs text-slate-500 mt-2">
                  Link expires at{" "}
                  <strong>
                    {new Date(result.expires_at).toLocaleString("en-GB")}
                  </strong>
                  .
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResult(null)}>
                Create another
              </Button>
              <Button onClick={onClose} data-testid="manual-booking-close-btn">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <Label htmlFor="mb-search">
                Member <span className="text-red-500">*</span>
              </Label>
              {selectedMember ? (
                <div
                  className="mt-1 flex items-start justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
                  data-testid="manual-booking-selected-member"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-emerald-900 truncate">
                      {selectedMember.last_name} {selectedMember.first_name}
                      {selectedMember.member_number && (
                        <span className="ml-2 text-xs text-blue-700 font-semibold">
                          #{selectedMember.member_number}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-emerald-800 truncate">{selectedMember.email}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-emerald-800 hover:bg-emerald-100"
                    onClick={clearMember}
                    data-testid="manual-booking-clear-member-btn"
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    id="mb-search"
                    type="text"
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    placeholder="Search by last name or email…"
                    autoComplete="off"
                    data-testid="manual-booking-member-search"
                  />
                  <div
                    className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100"
                    data-testid="manual-booking-member-results"
                  >
                    {memberQuery.trim().length < 2 ? (
                      <p className="text-xs text-slate-400 italic p-3 text-center">
                        Type at least 2 characters to search
                      </p>
                    ) : memberSearching ? (
                      <p className="text-xs text-slate-500 italic p-3 text-center">Searching…</p>
                    ) : memberResults.length === 0 ? (
                      <p className="text-xs text-slate-500 italic p-3 text-center">No matching members</p>
                    ) : (
                      memberResults.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => pickMember(m)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                          data-testid={`manual-booking-member-${m.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-800 truncate">
                                {m.last_name} {m.first_name}
                                {m.member_number && (
                                  <span className="ml-2 text-xs text-blue-600 font-semibold">
                                    #{m.member_number}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{m.email}</p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <div>
              <Label htmlFor="mb-reason">
                Booking reason <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.booking_reason}
                onValueChange={(v) => updateField("booking_reason", v)}
              >
                <SelectTrigger
                  id="mb-reason"
                  data-testid="manual-booking-reason"
                >
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isOther && (
              <div>
                <Label htmlFor="mb-reason-other">
                  Please specify <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="mb-reason-other"
                  value={form.booking_reason_other}
                  onChange={(e) =>
                    updateField("booking_reason_other", e.target.value)
                  }
                  placeholder="Describe the reason"
                  required
                  data-testid="manual-booking-reason-other"
                />
              </div>
            )}

            {event?.pricing_type === "custom_rows" && (
              <TicketCategoryPicker
                eventId={event.id}
                open={isOpen}
                value={form.custom_row_id}
                onChange={(v, cat) => setForm((p) => ({
                  ...p,
                  custom_row_id: v,
                  amount: p.amount === "" && cat?.amount ? String(cat.amount) : p.amount,
                }))}
                countsWhenPaid
                testIdPrefix="manual-booking"
              />
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mb-amount">
                  Amount (€) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="mb-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => updateField("amount", e.target.value)}
                  placeholder="0.00"
                  required
                  data-testid="manual-booking-amount"
                />
              </div>
              <div>
                <Label htmlFor="mb-expiry">Link expires in (minutes)</Label>
                <Input
                  id="mb-expiry"
                  type="number"
                  min="30"
                  max="1440"
                  step="5"
                  value={form.expires_in_minutes}
                  onChange={(e) =>
                    updateField("expires_in_minutes", e.target.value)
                  }
                  data-testid="manual-booking-expiry"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Stripe limit: 30–1440 min (24 h max).
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="mb-notes">
                Internal notes <span className="text-xs text-slate-500 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="mb-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Visible to admins only…"
                data-testid="manual-booking-notes"
              />
            </div>

            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>
                A Stripe payment link will be generated and emailed to the member.
                The booking stays <strong>pending</strong> until they pay; once Stripe confirms,
                it will appear as paid in the bookings list with the reason visible.
              </span>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                data-testid="manual-booking-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="manual-booking-submit-btn"
              >
                {isSubmitting && (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                )}
                Send payment link
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminManualBookingDialog;
